/**
 * `vat` - the VAT table read off the OCR lines, with no model.
 *
 * VAT tables are laid out every which way - `Moms% Moms Netto Brutto`,
 * `25% 174,22 871,12`, `Moms 25% (603,20 kr) 150,80 kr` - so the columns are
 * not read by their headers but by arithmetic: on a line that names a
 * Swedish rate, the VAT is the amount that is the rate's share of another
 * amount on it, as net (`net × rate`) or as gross (`gross × rate / (100 +
 * rate)`). A line where no two amounts agree like that is not a VAT row -
 * except `Moms 12 % 16,08`, a line labelled VAT with a rate and one amount,
 * taken with less confidence when no checked row has that rate.
 *
 * Needs `lines` (or `text`). Provides `candidates:vatLines`, one row per rate
 * found, when any is. No options.
 */

import type { Step } from '../types.js';
import { lineTexts } from './support.js';

const RATES = [25, 12, 6] as const;

/** Amounts with two decimals: `1 234,50`, `1.234,50`, `155.12`, `-3,00`. */
const AMOUNT = /(?<![\d.,])-?\d{1,3}(?:[ .]\d{3})*[.,]\d{2}(?![\d%])|(?<![\d.,])-?\d+[.,]\d{2}(?![\d%])/g;
/** A rate written as a percentage: `25%`, `25,0 %`, `12.00%`. */
const PERCENT = /(?<![\d.,])(25|12|6)(?:[.,]0{1,2})?\s?%/;

function amountsIn(text: string): number[] {
  return [...text.matchAll(AMOUNT)].map((match) => {
    const raw = match[0].replace(/\s/g, '');
    // The last separator is the decimal one; any before it groups thousands.
    const decimal = Math.max(raw.lastIndexOf(','), raw.lastIndexOf('.'));
    return Number(`${raw.slice(0, decimal).replace(/[.,]/g, '')}.${raw.slice(decimal + 1)}`);
  });
}

export interface VatRow {
  rate: number;
  vat: number;
  net: number | null;
  gross: number | null;
  /** Checked against a net or gross on the same line. */
  checked: boolean;
}

const close = (a: number, b: number) => Math.abs(a - b) <= Math.max(0.02, Math.abs(b) * 0.0005);

/** The VAT row a line holds, if it names a rate and two of its amounts agree on it. */
export function vatRowIn(text: string): VatRow | null {
  let amounts = amountsIn(text);
  let rate: number | null = null;
  const percent = PERCENT.exec(text);
  if (percent) {
    rate = Number(percent[1]);
  } else if (amounts.length >= 3 && (RATES as readonly number[]).includes(amounts[0]!)) {
    // `12,00 16,62 138,50 155,12`: the rate printed as the row's first number.
    rate = amounts[0]!;
    amounts = amounts.slice(1);
  }
  if (rate === null) return null;
  const positive = amounts.map(Math.abs);
  for (const vat of positive) {
    const net = positive.find((other) => other > vat && close(vat, (other * rate!) / 100)) ?? null;
    const gross = positive.find((other) => other > vat && close(vat, (other * rate!) / (100 + rate!))) ?? null;
    if (net !== null || gross !== null) return { rate, vat, net, gross, checked: true };
  }
  // `Moms 12 % 16,08`: labelled VAT, one amount, nothing to check it against.
  if (percent && positive.length === 1 && /moms|vat|mva/i.test(text)) return { rate, vat: positive[0]!, net: null, gross: null, checked: false };
  return null;
}

export const vatStep: Step<Record<string, never>> = {
  name: 'vat',
  needs: () => ['lines'],
  provides: () => ['candidates:vatLines'],
  async run(state, context) {
    const texts = lineTexts(state);
    const rows = new Map<number, VatRow>();
    for (const text of texts.length > 0 ? texts : (state.input.text ?? '').split('\n')) {
      const row = vatRowIn(text);
      if (row && (!rows.has(row.rate) || (row.checked && !rows.get(row.rate)!.checked))) rows.set(row.rate, row);
    }
    if (rows.size === 0) return { skipped: 'no VAT row found' };
    // As a model answers: amounts as strings, so `normalizeExtraction` reads either the same way.
    const format = (value: number | null) => (value === null ? null : value.toFixed(2).replace('.', ','));
    const value = [...rows.values()].map((row) => ({
      rate: String(row.rate),
      net: format(row.net),
      vat: format(row.vat),
      gross: format(row.gross),
    }));
    return {
      candidates: { vatLines: [{ value, source: context.id, confidence: [...rows.values()].every((row) => row.checked) ? 1 : 0.5 }] },
      added: value.map((row) => `${row.rate}%: moms ${row.vat}${row.gross ? ` of ${row.gross}` : row.net ? ` on ${row.net}` : ''}`),
    };
  },
};
